import { getPaginatedScheduledNotifications } from '../../../api/ScheduledNotificationsApi'
import {
  getCorporateCreditsDistributionScheduleById,
  getCorporateCreditsDistributionScheduleHistories,
  getCorporateCreditsDistributionScheduleMembers,
} from '../../../api/corporateApi'
import YesOrNoTag from '../../../components/common/YesOrNoTag'
import CorporateCreditsDistributionScheduleHistoriesTable from '../../../components/corporates/corporate-credits-distribution-schedule-histories-table/corporate-credits-distribution-schedule-histories-table'
import CorporateCreditsDistributionScheduleMemberTable from '../../../components/corporates/corporate-credits-distribution-schedule-member-table/corporate-credits-distribution-schedule-member-table'
import LinkButton from '../../../components/link-button/link-button'
import ScheduledNotificationsTable from '../../../components/scheduled-notifications/scheduled-notifications-table'
import {
  IPaginatedScheduledNotificationsParams,
  IScheduledNotification,
} from '../../../components/scheduled-notifications/share-type'
import { formatDuration } from '../../../helpers/corporate-credits-distribution-schedule-helper'
import { formatNumberToTime, getDateTime, getSearchParams } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import {
  CorporateCreditsDistributionScheduleType,
  ICorporateCreditsDistributionSchedule,
  ICorporateCreditsDistributionScheduleHistory,
  ICorporateCreditsDistributionScheduleMember,
} from '../share-type'
import { Descriptions, Space } from 'antd'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

const defaultScheduledNotificationPageInfoPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const scheduledNotificationSubjectType = 'CORPORATE_CREDITS_DISTRIBUTION_SCHEDULE'

const CorporateCreditsDistributionSchedule = () => {
  const params = useParams()
  const [corporateId] = useState<number>(() => Number(params?.id))
  const [creditsDistributionScheduleId] = useState<number>(() =>
    Number(params?.creditsDistributionScheduleId),
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const [scheduledNotifications, setScheduledNotifications] = useState<IScheduledNotification[]>([])
  const [scheduledNotificationPageInfo, setScheduledNotificationPageInfo] = useState<PageInfo>(
    defaultScheduledNotificationPageInfoPage,
  )

  const [corporateCreditsDistributionSchedule, setCorporateCreditsDistributionSchedule] =
    useState<ICorporateCreditsDistributionSchedule>()

  const [
    corporateCreditsDistributionScheduleMembers,
    setCorporateCreditsDistributionScheduleMembers,
  ] = useState<ICorporateCreditsDistributionScheduleMember[]>([])

  const [
    corporateCreditsDistributionScheduleHistories,
    setCorporateCreditsDistributionScheduleHistories,
  ] = useState<ICorporateCreditsDistributionScheduleHistory[]>([])

  useEffect(() => {
    if (corporateId && creditsDistributionScheduleId) {
      getCorporateCreditsDistributionScheduleById(corporateId, creditsDistributionScheduleId).then(
        res => {
          if (!res.isApplyForAllMembers) {
            getCorporateCreditsDistributionScheduleMembersAction()
          }
          setCorporateCreditsDistributionSchedule(res)
        },
      )
      getCorporateCreditsDistributionScheduleHistoriesAction()
    }
  }, [corporateId, creditsDistributionScheduleId])

  useEffect(() => {
    const searchScheduledNotificationPageSize = searchParams.get('scheduledNotificationPageSize')
    const searchScheduledNotificationPage = searchParams.get('scheduledNotificationPage')
    getPaginatedScheduledNotificationsAction({
      size:
        searchScheduledNotificationPageSize === null
          ? defaultScheduledNotificationPageInfoPage.pageSize
          : Number(searchScheduledNotificationPageSize),
      page:
        searchScheduledNotificationPage === null
          ? defaultScheduledNotificationPageInfoPage.currentPage - 1
          : Number(searchScheduledNotificationPage),
      subjectId: creditsDistributionScheduleId,
      subjectType: scheduledNotificationSubjectType,
    })
  }, [])

  const getCorporateCreditsDistributionScheduleMembersAction = () => {
    if (corporateId && creditsDistributionScheduleId) {
      getCorporateCreditsDistributionScheduleMembers(
        corporateId,
        creditsDistributionScheduleId,
      ).then(members => {
        setCorporateCreditsDistributionScheduleMembers(members)
      })
    }
  }

  const getCorporateCreditsDistributionScheduleHistoriesAction = () => {
    if (corporateId && creditsDistributionScheduleId) {
      getCorporateCreditsDistributionScheduleHistories(
        corporateId,
        creditsDistributionScheduleId,
      ).then(histories => {
        setCorporateCreditsDistributionScheduleHistories(histories)
      })
    }
  }

  const getPaginatedScheduledNotificationsAction = (
    params: IPaginatedScheduledNotificationsParams,
  ) => {
    getPaginatedScheduledNotifications(params).then(res => {
      if (res && res.pageInfo && res.data) {
        setScheduledNotificationPageInfo({
          ...res.pageInfo,
          currentPage: res.pageInfo.currentPage + 1,
        })
        setScheduledNotifications(res.data)
      }
    })
  }

  const scheduledNotificationPaginationOnChange = (page: number, pageSize: number) => {
    if (creditsDistributionScheduleId) {
      const params = {
        size: pageSize,
        page: page - 1,
        subjectId: creditsDistributionScheduleId,
        subjectType: scheduledNotificationSubjectType,
      }
      getPaginatedScheduledNotificationsAction(params)
      setSearchParams(
        getSearchParams({
          ...searchParams,
          scheduledNotificationPageSize: pageSize,
          scheduledNotificationPage: page - 1,
        }),
      )
    }
  }

  return (
    <>
      <Space style={{ width: '100%' }} orientation="vertical" size={16}>
        {corporateCreditsDistributionSchedule && (
          <>
            <Descriptions
              title={translate('navbar.corporateCreditsDistributionSchedule.view')}
              styles={{
                label: { alignItems: 'center', fontWeight: 600, width: '200px' },
                content: { minWidth: 300 },
              }}
              column={2}
              bordered
              extra={
                <Space>
                  <AuthPermission
                    permission={PermissionsEnum.CORPORATE_CREDITS_DISTRIBUTION_SCHEDULES_WRITE}
                  >
                    <LinkButton
                      to={`/corporates/${corporateId}/credits-distribution-schedules/${corporateCreditsDistributionSchedule.id}/edit`}
                    >
                      {translate('edit')}
                    </LinkButton>
                  </AuthPermission>
                </Space>
              }
            >
              <Descriptions.Item label="ID">
                {corporateCreditsDistributionSchedule.id}
              </Descriptions.Item>
              <Descriptions.Item label="Corporate ID">
                <LinkButton to={`/corporates/${corporateCreditsDistributionSchedule.corporateId}`}>
                  {corporateCreditsDistributionSchedule.corporateId}
                </LinkButton>
              </Descriptions.Item>

              <Descriptions.Item label={translate('isActive')}>
                <YesOrNoTag value={corporateCreditsDistributionSchedule.isActive} />
              </Descriptions.Item>

              <Descriptions.Item
                label={translate(
                  'corporate.corporateCreditsDistributionSchedule.modal.isApplyForAllMembers',
                )}
              >
                <YesOrNoTag value={corporateCreditsDistributionSchedule.isApplyForAllMembers} />
              </Descriptions.Item>

              <Descriptions.Item label={translate('corporate.credits')}>
                {corporateCreditsDistributionSchedule.credits}
              </Descriptions.Item>

              <Descriptions.Item label={translate('corporate.bufferType')}>
                {corporateCreditsDistributionSchedule.bufferType}
              </Descriptions.Item>

              <Descriptions.Item label={translate('corporate.bufferFixedValue')}>
                {corporateCreditsDistributionSchedule.bufferFixedValue}
              </Descriptions.Item>

              <Descriptions.Item label={translate('corporate.bufferPercentage')}>
                {corporateCreditsDistributionSchedule.bufferPercentage}%
              </Descriptions.Item>

              <Descriptions.Item
                label={translate(
                  'corporate.corporateCreditsDistributionSchedule.modal.creditsValidityPeriod',
                )}
              >
                {formatDuration(corporateCreditsDistributionSchedule?.creditsValidityPeriod)}
              </Descriptions.Item>
              <Descriptions.Item
                label={translate(
                  'corporate.corporateCreditsDistributionSchedule.modal.isReclaimBeforeDistribute',
                )}
              >
                <YesOrNoTag
                  value={corporateCreditsDistributionSchedule.isReclaimBeforeDistribute}
                />
              </Descriptions.Item>

              <Descriptions.Item
                label={translate(
                  'corporate.corporateCreditsDistributionSchedule.modal.schedule.type',
                )}
              >
                {corporateCreditsDistributionSchedule?.schedule.type}
              </Descriptions.Item>

              {corporateCreditsDistributionSchedule?.schedule.type ===
                CorporateCreditsDistributionScheduleType.SPECIFIC_TIME && (
                <Descriptions.Item
                  label={translate(
                    'corporate.corporateCreditsDistributionSchedule.modal.schedule.specificTimes',
                  )}
                >
                  {corporateCreditsDistributionSchedule?.schedule.specific_times?.map(
                    (time, index) => (
                      <div key={index}>{time}</div>
                    ),
                  )}
                </Descriptions.Item>
              )}

              {corporateCreditsDistributionSchedule?.schedule.type ===
                CorporateCreditsDistributionScheduleType.REPEATABLE && (
                <>
                  <Descriptions.Item
                    label={translate(
                      'corporate.corporateCreditsDistributionSchedule.modal.schedule.interval',
                    )}
                  >
                    {formatDuration(corporateCreditsDistributionSchedule?.schedule)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={translate(
                      'corporate.corporateCreditsDistributionSchedule.modal.schedule.startDate',
                    )}
                  >
                    {corporateCreditsDistributionSchedule?.schedule.start_date}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={translate(
                      'corporate.corporateCreditsDistributionSchedule.modal.schedule.endDate',
                    )}
                  >
                    {corporateCreditsDistributionSchedule?.schedule.end_date}
                  </Descriptions.Item>
                </>
              )}

              <Descriptions.Item
                label={translate('corporate.corporateCreditsDistributionSchedule.modal.isSendSms')}
              >
                <YesOrNoTag value={corporateCreditsDistributionSchedule.isSendSms} />
              </Descriptions.Item>

              <Descriptions.Item
                label={translate(
                  'corporate.corporateCreditsDistributionSchedule.modal.smsSendTime',
                )}
              >
                {corporateCreditsDistributionSchedule.smsSendTime
                  ? formatNumberToTime(corporateCreditsDistributionSchedule.smsSendTime)
                  : '-'}
              </Descriptions.Item>

              <Descriptions.Item label={translate('corporate.notes')} span={2}>
                {corporateCreditsDistributionSchedule.notes}
              </Descriptions.Item>

              <Descriptions.Item label={translate('createdAt')}>
                {getDateTime(corporateCreditsDistributionSchedule.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label={translate('updatedAt')}>
                {getDateTime(corporateCreditsDistributionSchedule.updatedAt)}
              </Descriptions.Item>
            </Descriptions>

            {!corporateCreditsDistributionSchedule.isApplyForAllMembers &&
              corporateCreditsDistributionScheduleMembers.length > 0 && (
                <CorporateCreditsDistributionScheduleMemberTable
                  corporateId={corporateId}
                  corporateCreditsDistributionScheduleId={creditsDistributionScheduleId}
                  members={corporateCreditsDistributionScheduleMembers}
                  onRefresh={getCorporateCreditsDistributionScheduleMembersAction}
                />
              )}

            <CorporateCreditsDistributionScheduleHistoriesTable
              histories={corporateCreditsDistributionScheduleHistories}
            />

            <ScheduledNotificationsTable
              data={scheduledNotifications}
              pageInfo={scheduledNotificationPageInfo}
              paginationOnChange={scheduledNotificationPaginationOnChange}
            />
          </>
        )}
      </Space>
    </>
  )
}

export default CorporateCreditsDistributionSchedule
